import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box, Chip, Divider, List, ListItemButton, ListItemText, Paper, Typography } from '@mui/material';
import { getPendingLeadsByCounselor } from '../../api/dashboard';
import { useBranchFilter } from '../../context/branchContext';
import { useNewLeadStatusId } from '../../hooks/useNewLeadStatusId';
import { EmptyState } from '../../components/EmptyState';

/** Pending Leads by Counselor — a workload/action-monitoring widget for
 * Super Admin/Lead Manager, distinct from the (untouched) Counselor
 * Leaderboard: this is a current-workload count, never called a ranking
 * or performance score (spec §10/§11). Grouped by the lead's *current*
 * assignedTo, never createdBy (spec §3) — enforced entirely on the
 * backend (GET /dashboard/pending-leads-by-counselor, gated on
 * leads:assign), not just hidden here.
 *
 * This card counts ONLY status "New" leads (the backend's
 * PENDING_LEAD_BY_COUNSELOR_WHERE) — deliberately narrower than the
 * Leads list's own generic `pending=true` filter (which several *other*
 * widgets' click-throughs still use unchanged). The click-through below
 * therefore passes an explicit `leadStatusId` for "New" (looked up via
 * the same `/master-data/lead-statuses` endpoint the Leads list's own
 * Status filter already uses — never a hardcoded id), so what a user
 * sees after clicking always matches what this card counted.
 *
 * Dashboard Counselor-sections inactive-employee exclusion — inactive
 * Counselors are excluded entirely (backend query, not a frontend
 * filter): they never appear here, and reappear automatically the
 * moment they're reactivated. Their still-pending leads are not lost —
 * the separate, dedicated `InactiveCounselorLeadsCard` on this same
 * dashboard already surfaces exactly those, with its own "Transfer
 * leads" action. */
export const PendingLeadsByCounselorCard = () => {
  const navigate = useNavigate();
  const { selectedBranchId } = useBranchFilter();
  const query = useQuery({
    queryKey: ['dashboard', 'pending-leads-by-counselor', selectedBranchId],
    queryFn: () => getPendingLeadsByCounselor(selectedBranchId),
  });
  const newStatusId = useNewLeadStatusId();
  const data = query.data;

  const activeCounselors = data?.counselors ?? [];
  const unassigned = data?.unassignedPendingLeadCount ?? 0;
  const isEmpty = activeCounselors.every((c) => c.pendingLeadCount === 0) && unassigned === 0;

  const goToCounselorPendingLeads = (counselorId: number) =>
    navigate(
      newStatusId
        ? `/leads?assignedToId=${counselorId}&leadStatusId=${newStatusId}`
        : `/leads?assignedToId=${counselorId}&pending=true`,
    );

  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Pending leads by Counselor
      </Typography>
      {isEmpty ? (
        <EmptyState loading={query.isLoading} message="No pending leads" />
      ) : (
        <>
          <List dense disablePadding>
            {activeCounselors.map((c) => (
              <ListItemButton
                key={c.id}
                onClick={() => goToCounselorPendingLeads(c.id)}
                sx={{ borderRadius: 2, px: 1 }}
              >
                <ListItemText primary={`${c.firstName} ${c.lastName}`} />
                <Box>
                  <Chip size="small" label={c.pendingLeadCount} color={c.pendingLeadCount > 0 ? 'primary' : 'default'} />
                </Box>
              </ListItemButton>
            ))}
            <Divider sx={{ my: 0.5 }} />
            <ListItemButton onClick={() => navigate('/lead-distribution')} sx={{ borderRadius: 2, px: 1 }}>
              <ListItemText primary="Unassigned" />
              <Box>
                <Chip size="small" label={unassigned} color={unassigned > 0 ? 'warning' : 'default'} />
              </Box>
            </ListItemButton>
          </List>
        </>
      )}
    </Paper>
  );
};
